import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useQuery } from "@apollo/client/react";
import { Delete as RemoveIcon } from "@mui/icons-material";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";

import { serverStrftime } from "../../../utils/dates";
import useMutation from "../../../hooks/useMutation";
import {
  INVENTORY_REQUEST_ATTACHMENT_MUTATION,
  INTERNAL_WORK_ORDER_ATTACHMENTS_QUERY,
} from "../../../constants/graphql/queries";
import {
  ADD_INVENTORY_REQUEST_ATTACHMENT_MUTATION,
  REMOVE_INVENTORY_REQUEST_ATTACHMENT_MUTATION,
  ADD_INTERNAL_WORK_ORDER_ATTACHMENT_MUTATION,
  REMOVE_INTERNAL_WORK_ORDER_ATTACHMENT_MUTATION,
} from "../../../constants/graphql/mutations";

const CONFIG = {
  inventoryRequest: {
    idKey: "inventoryRequestId",
    query: INVENTORY_REQUEST_ATTACHMENT_MUTATION,
    resultKey: "inventoryRequestAttachments",
    addMutation: ADD_INVENTORY_REQUEST_ATTACHMENT_MUTATION,
    removeMutation: REMOVE_INVENTORY_REQUEST_ATTACHMENT_MUTATION,
    buildAddVariables: (id, file) => ({ inventoryRequestId: id, file }),
    buildRemoveVariables: (id, attachmentId) => ({ inventoryRequestId: id, id: attachmentId }),
    getAttachments: data => data?.inventoryRequestAttachments || [],
  },
  internalWorkOrder: {
    idKey: "id",
    query: INTERNAL_WORK_ORDER_ATTACHMENTS_QUERY,
    resultKey: "internalWorkOrder",
    addMutation: ADD_INTERNAL_WORK_ORDER_ATTACHMENT_MUTATION,
    removeMutation: REMOVE_INTERNAL_WORK_ORDER_ATTACHMENT_MUTATION,
    buildAddVariables: (id, file) => ({ internalWorkOrderId: id, file }),
    buildRemoveVariables: (id, attachmentId) => ({ internalWorkOrderId: id, id: parseInt(attachmentId) }),
    getAttachments: data => data?.internalWorkOrder?.attachments || [],
  },
};

export default function Attachments({ disabled, entityId, entityType = "inventoryRequest" }) {
  const config = CONFIG[entityType];

  if (!config) {
    throw new Error(`Unsupported attachments entity: ${entityType}`);
  }

  const {
    idKey,
    query,
    getAttachments,
    addMutation,
    removeMutation,
    buildAddVariables,
    buildRemoveVariables,
  } = config;

  const { data: queryData = {}, refetch, loading } = useQuery(query, {
    variables: { [idKey]: entityId },
    skip: !entityId,
  });

  const attachments = getAttachments(queryData);

  const [addAttachment, { loading: saving }] = useMutation(addMutation);
  const [removeAttachment, { loading: removing }] = useMutation(removeMutation);

  const onDrop = useCallback(
    acceptedFiles => {
      Promise.all(
        acceptedFiles.map(file =>
          addAttachment({
            variables: buildAddVariables(entityId, file),
          })
        )
      ).then(() => refetch());
    },
    [entityId, addAttachment, buildAddVariables, refetch]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleRemove = useCallback(
    id => {
      removeAttachment({
        variables: buildRemoveVariables(entityId, id),
      }).then(() => refetch());
    },
    [removeAttachment, buildRemoveVariables, entityId, refetch]
  );

  return (
    <Box>
      <Box height={16} pt={0.75}>
        {(loading || saving || removing) && <LinearProgress />}
      </Box>
      {attachments.length > 0 && (
        <Box mb={2}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell align="right">Date</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {attachments.map((attachment, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Link target="_blank" href={attachment.url}>
                        {attachment.fileName || "No name"}
                      </Link>
                    </TableCell>
                    <TableCell align="right">{serverStrftime(attachment.createdAt)}</TableCell>
                    <TableCell align="right">
                      {!disabled && (
                        <IconButton size="small" onClick={() => handleRemove(attachment.id)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <div {...getRootProps()}>
        <Button disabled={disabled} fullWidth variant="outlined" style={{ textTransform: "none" }}>
          <Box p={2} display="flex" justifyContent="center">
            <input {...getInputProps()} />
            {isDragActive ? (
              <Typography>Drop the files here ...</Typography>
            ) : (
              <Typography>Add Attachments</Typography>
            )}
          </Box>
        </Button>
      </div>
    </Box>
  );
}
